import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Users, Star, Search, SlidersHorizontal } from 'lucide-react';
interface PropertyBrowserProps {
  onSelectProperty: (propertyId: string) => void;
}
const PROPERTIES = [{
  id: 'moon-palace-cancun',
  name: 'Moon Palace Cancún',
  location: 'Cancún, Mexico',
  rooms: 2500,
  type: 'All-Inclusive',
  price: 189,
  image: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&q=80&fit=crop'
}, {
  id: 'grand-moon-palace',
  name: 'The Grand at Moon Palace',
  location: 'Cancún, Mexico',
  rooms: 1300,
  type: 'Premium All-Inclusive',
  price: 249,
  image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80&fit=crop'
}, {
  id: 'moon-palace-jamaica',
  name: 'Moon Palace Jamaica',
  location: 'Ocho Rios, Jamaica',
  rooms: 700,
  type: 'All-Inclusive',
  price: 199,
  image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800&q=80&fit=crop'
}, {
  id: 'le-blanc-cancun',
  name: 'Le Blanc Spa Resort Cancún',
  location: 'Cancún, Mexico',
  rooms: 260,
  type: 'Adults-Only Luxury',
  price: 399,
  image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&q=80&fit=crop'
}, {
  id: 'le-blanc-los-cabos',
  name: 'Le Blanc Spa Resort Los Cabos',
  location: 'Los Cabos, Mexico',
  rooms: 373,
  type: 'Adults-Only Luxury',
  price: 449,
  image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&q=80&fit=crop'
}, {
  id: 'nizuc-resort',
  name: 'Nizuc Resort & Spa',
  location: 'Cancún, Mexico',
  rooms: 274,
  type: 'Luxury',
  price: 350,
  image: 'https://images.unsplash.com/photo-1540541338287-41700207dee6?w=800&q=80&fit=crop'
}];
export function PropertyBrowser({
  onSelectProperty
}: PropertyBrowserProps) {
  const [destination, setDestination] = useState('All');
  const filteredProperties = PROPERTIES.filter(p => destination === 'All' || p.location.includes(destination));
  return <div className="space-y-12 animate-in fade-in duration-500 pb-24" data-id="element-261">
      {/* Hero Search */}
      <div className="bg-stone-900 text-stone-50 py-16 px-6 rounded-xl relative overflow-hidden" data-id="element-262">
        <div className="absolute inset-0 bg-gradient-to-r from-stone-900 to-stone-800 z-0" data-id="element-263" />
        <div className="relative z-10 max-w-3xl mx-auto text-center space-y-8" data-id="element-264">
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight" data-id="element-265">
            Find your perfect group destination
          </h1>

          <div className="bg-white p-2 rounded-lg flex flex-col md:flex-row gap-2 shadow-xl" data-id="element-266">
            <div className="flex-1 flex items-center px-4 py-3 bg-stone-50 rounded-md border border-stone-200" data-id="element-267">
              <Search className="w-5 h-5 text-stone-400 mr-3" data-id="element-268" />
              <input type="text" placeholder="Search resorts or destinations..." className="bg-transparent border-none outline-none text-stone-900 w-full placeholder:text-stone-400" data-id="element-269" />
            </div>
            <button className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-md font-medium transition-colors whitespace-nowrap" data-id="element-270">
              Search
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 border-b border-stone-200 pb-6" data-id="element-271">
        <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0" data-id="element-272">
          {['All', 'Mexico', 'Jamaica', 'Dominican Republic'].map(dest => <button key={dest} onClick={() => setDestination(dest)} className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${destination === dest ? 'bg-stone-900 text-white' : 'bg-white border border-stone-200 text-stone-600 hover:bg-stone-50'}`} data-id="element-273">
              {dest}
            </button>)}
        </div>
        <button className="flex items-center gap-2 text-sm font-medium text-stone-600 hover:text-stone-900 bg-white border border-stone-200 px-4 py-2 rounded-md transition-colors w-full md:w-auto justify-center" data-id="element-274">
          <SlidersHorizontal className="w-4 h-4" data-id="element-275" />
          More Filters
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-276">
        {filteredProperties.map((property, i) => <motion.div key={property.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.1
      }} className="bg-white border border-stone-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all group flex flex-col" data-id="element-277">
            {/* Image */}
            <div className="h-48 relative overflow-hidden" data-id="element-278">
              <img src={property.image} alt={property.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-279" />
              <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-full flex items-center gap-1" data-id="element-280">
                <Star className="w-3 h-3 text-amber-500 fill-amber-500" data-id="element-281" />
                <span className="text-xs font-medium text-stone-900" data-id="element-282">4.8</span>
              </div>
            </div>

            <div className="p-6 flex-1 flex flex-col" data-id="element-283">
              <div className="flex justify-between items-start mb-2" data-id="element-284">
                <h3 className="font-serif text-xl text-stone-900 line-clamp-1" data-id="element-285">
                  {property.name}
                </h3>
              </div>

              <div className="flex items-center gap-1.5 text-stone-500 text-sm mb-4" data-id="element-286">
                <MapPin className="w-4 h-4" data-id="element-287" />
                <span data-id="element-288">{property.location}</span>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-289">
                <div data-id="element-290">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-400 mb-1" data-id="element-291">
                    Inventory
                  </p>
                  <p className="flex items-center gap-1.5 text-sm font-medium text-stone-700" data-id="element-292">
                    <Users className="w-4 h-4 text-stone-400" data-id="element-293" />
                    {property.rooms} rooms
                  </p>
                </div>
                <div data-id="element-294">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-400 mb-1" data-id="element-295">
                    Experience
                  </p>
                  <p className="text-sm font-medium text-stone-700 line-clamp-1" data-id="element-296">
                    {property.type}
                  </p>
                </div>
              </div>

              <div className="mt-auto pt-6 border-t border-stone-100 flex items-center justify-between" data-id="element-297">
                <div data-id="element-298">
                  <p className="text-xs text-stone-500" data-id="element-299">Starting from</p>
                  <p className="font-mono text-lg text-stone-900" data-id="element-300">
                    ${property.price}
                    <span className="text-sm text-stone-500" data-id="element-301">/nt</span>
                  </p>
                </div>
                <button onClick={() => onSelectProperty(property.id)} className="bg-stone-900 hover:bg-stone-800 text-white px-5 py-2.5 rounded-md text-sm font-medium transition-colors" data-id="element-302">
                  Get Instant Quote
                </button>
              </div>
            </div>
          </motion.div>)}
      </div>
    </div>;
}